/**
 * Profile Hooks for AmalQ
 * React Query hooks for profile operations
 * Following enterprise-grade patterns from existing codebase
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { profileApi } from '../services/profileApi';
import { profileQueryKeys } from './profileQueryKeys';
import {
  SectionCompletionQuery,
  EmailVerificationStatusResponse,
} from '../types/profile.types';
import { useAuth } from '../../../src/hooks/useAuth';

/**
 * Hook: Get user profile
 * Fetches user profile data with caching
 * Only runs when user is authenticated to prevent unnecessary 401 errors
 */
export function useProfile() {
  const { authState } = useAuth();
  const isAuthenticated = authState.isAuthenticated && !!authState.user;

  return useQuery({
    queryKey: profileQueryKeys.profile(),
    queryFn: profileApi.getProfile,
    enabled: isAuthenticated, // Only run query when user is authenticated
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: 1000,
  });
}

/**
 * Hook: Get profile completion
 * Fetches profile completion percentage and missing fields
 */
export function useProfileCompletion() {
  return useQuery({
    queryKey: profileQueryKeys.completion(),
    queryFn: profileApi.getProfileCompletion,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    retry: 2,
    retryDelay: 1000,
  });
}

/**
 * Hook: Get verification status
 * Fetches user verification status (email, phone, 2FA)
 * Uses browser cache for instant hydration on page load
 * Only runs when user is authenticated to prevent unnecessary 401 errors
 */
export function useVerificationStatus() {
  const { authState } = useAuth();
  const isAuthenticated = authState.isAuthenticated && !!authState.user;

  return useQuery({
    queryKey: profileQueryKeys.verificationStatus(),
    queryFn: () => profileApi.getVerificationStatus(),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error && typeof error === 'object' && 'statusCode' in error && error.statusCode === 401) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: 1000,
  });
}

/**
 * Hook: Update profile mutation
 * Handles profile updates with optimistic updates
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: profileApi.updateProfile,
    onSuccess: () => {
      // Invalidate profile queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.profile() });
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.completion() });
    },
    onError: (error) => {
      console.error('Profile update failed:', error);
    },
  });
}

/**
 * Hook: Upload avatar mutation
 * Handles avatar upload with progress tracking
 */
export function useUploadAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: profileApi.uploadAvatar,
    onSuccess: () => {
      // Invalidate profile queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.profile() });
    },
    onError: (error) => {
      console.error('Avatar upload failed:', error);
    },
  });
}

/**
 * Hook: Delete avatar mutation
 * Handles avatar deletion
 */
export function useDeleteAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: profileApi.deleteAvatar,
    onSuccess: () => {
      // Invalidate profile queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.profile() });
    },
    onError: (error) => {
      console.error('Avatar deletion failed:', error);
    },
  });
}

/**
 * Hook: Upload organization logo mutation
 * Handles organization logo upload
 */
export function useUploadOrganizationLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: profileApi.uploadOrganizationLogo,
    onSuccess: () => {
      // Invalidate profile queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.profile() });
    },
    onError: (error) => {
      console.error('Organization logo upload failed:', error);
    },
  });
}

/**
 * Hook: Get enhanced profile completion
 * Fetches section-specific completion data with detailed analytics
 */
export function useEnhancedProfileCompletion(query?: SectionCompletionQuery) {
  return useQuery({
    queryKey: profileQueryKeys.enhancedCompletion(query),
    queryFn: () => profileApi.getEnhancedProfileCompletion(query),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: 1000,
  });
}

/**
 * Hook: Get section-specific completion
 * Fetches completion data for a specific profile section
 */
export function useSectionCompletion(section: 'personal' | 'organizer' | 'organization' | 'preferences') {
  return useQuery({
    queryKey: profileQueryKeys.sectionCompletion(section),
    queryFn: () => profileApi.getSectionCompletion(section),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: 1000,
  });
}

/**
 * Hook: Get email verification status
 * Fetches email verification status and resend eligibility
 * Enhanced error handling for 401 (token expiration) and network failures
 * Uses browser cache for instant hydration on page load
 * Only runs when user is authenticated to prevent unnecessary 401 errors
 */
export function useEmailVerificationStatus() {
  const { authState } = useAuth();
  const isAuthenticated = authState.isAuthenticated && !!authState.user;

  return useQuery<EmailVerificationStatusResponse['data'], Error>({
    queryKey: ['email-verification-status'],
    queryFn: async () => {
      const res = await profileApi.getEmailVerificationStatus();
      const emailData = res.data;

      // Tolerate both flat and nested response shapes from backend.
      if (
        emailData &&
        typeof emailData === 'object' &&
        'data' in emailData &&
        emailData.data &&
        typeof emailData.data === 'object' &&
        'emailVerified' in emailData.data
      ) {
        return emailData.data as EmailVerificationStatusResponse['data'];
      }
      return emailData as EmailVerificationStatusResponse['data'];
    },
    enabled: isAuthenticated,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error && typeof error === 'object' && 'statusCode' in error && error.statusCode === 401) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook: Resend verification email mutation
 * Handles resending verification email with error handling
 */
export function useResendVerificationEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => profileApi.resendVerificationEmail(),
    onSuccess: () => {
      // Invalidate verification status to refetch fresh data
      queryClient.invalidateQueries({ queryKey: ['email-verification-status'] });
    },
    onError: (error) => {
      console.error('Resend verification email failed:', error);
    },
  });
}

/**
 * Hook: Verify email code
 * Submits 6-digit code to verify email and invalidates related queries
 * Forces immediate refetch to update UI instantly after verification
 */
export function useVerifyEmailCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (code: string) => profileApi.verifyEmailCode(code),
    onSuccess: () => {
      // Invalidate queries to mark them as stale
      queryClient.invalidateQueries({ queryKey: ['email-verification-status'] });
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.profile() });
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.verificationStatus() });
      
      // Force immediate refetch to update UI instantly
      // This ensures the banner disappears immediately after verification
      queryClient.refetchQueries({ queryKey: ['email-verification-status'] });
      queryClient.refetchQueries({ queryKey: profileQueryKeys.profile() });
      queryClient.refetchQueries({ queryKey: profileQueryKeys.verificationStatus() });
    },
    onError: (error) => {
      console.error('Verify email code failed:', error);
    },
  });
}
